import {
  Component,
  EventEmitter,
  inject,
  Input,
  model,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ModalService } from '@app/components/modal/modal.service';
import { NgFor, NgIf } from '@angular/common';
import { NewFinancialProductTemplateComponent } from '../new-financial-product-template/new-financial-product-template.component';
import { AppCommonModule } from '../../../../../../../components/app.common.module';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { currencies, productTypeEnum } from '@app/pages/quote/open-quote/model/open-quote.model';
import { FinancialProductService } from '../../financial-product.service';
import { BaseComponent } from '@app/components/base/base.component';
import { TippyDirective } from '@ngneat/helipopper';
import { SubBrandService } from '@app/pages/quote/standard-quote/catalogs/sub-brand/sub-brand.service';
import { FileType } from '@app/models/fileTypes.enum';
import { FinancialCalculatorService } from '@app/pages/quote/open-quote/services/financial-calculator.service';
import { finalize, interval, take } from 'rxjs';
import { NotificationService } from '@app/services/notifications.service';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-new-financial-product',
  standalone: true,
  templateUrl: './new-financial-product.component.html',
  styleUrl: './new-financial-product.component.scss',
  imports: [
    NewFinancialProductTemplateComponent,
    NgFor,
    NgIf,
    AppCommonModule,
    TippyDirective,
    TranslateModule
  ],
})
export class NewFinancialProductComponent extends BaseComponent implements OnInit {
  public productImg = signal('');
  private readonly subBrandService = inject(SubBrandService);
  public readonly financialCalculator = inject(FinancialCalculatorService);
  public readonly translateService = inject(TranslateService);


  residualValue: any[] = [];
  subBrands: any[] = [];
  brands: any[] = [];
  form: FormGroup;
  public currencies = currencies;
  isLoading: boolean = false;
  data: any[] = [];
  id: number;

  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @Output() saveResidualValue = new EventEmitter<any>();
  @Input() hasData: boolean;

  constructor(
    private modalService: ModalService,
    private fb: FormBuilder,
    private router: Router,
    private service: FinancialProductService,
    private _route: ActivatedRoute,
    private notificationsService: NotificationService
  ) {
    super();
    this.createForm();
  }

  ngOnInit() {
    // this.getSubBrands();
    this.getBrands();
    this.createForm();
    this._route.paramMap.subscribe((p) => {
      this.id = Number(p.get('id')) ?? null;
      if (this.id) {
        this.getModelById();
      }
    });
  }

  changeSubBrand(event: Event) {
    const target = event?.target as HTMLSelectElement;
    const value = target?.value;
    if (!event && !value) return;
    this.getFile(Number(value) || 0);
  }

  getFile(id: number) {
    if (id === 0) return;
    this.isLoading = true;
    this.subBrandService.getAttachmentByElementId(id, FileType.SUB_BRAND).subscribe({
      next: (response: any) => {
        if (response.data.length > 0) {
          const row = response.data[0];
          this.productImg.set(row.publicUrl || row.urlSource || '');
        } else {
          this.productImg.set('');
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.productImg.set('');
        if (error.status === 404) {
          return;
        }
      },
    });
  }

  createForm() {
    this.form = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      modelType: ['', Validators.required],
      brandId: ['', Validators.required],
      subBrandId: ['', Validators.required],
      classification: ['', Validators.required],
      year: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\d{4}$/), //Deben ser 4 numeros
          Validators.max(new Date().getFullYear() + 1), // Fecha maxima +1 del año actual
          Validators.min(new Date().getFullYear() - 9), //Fecha minima -9 del año actual
        ],
      ],
      price: [
        '',
        [
          Validators.required,
          Validators.min(0), // Precio debe ser positivo
          Validators.pattern(/^\d+(\.\d{1,2})?$/), // Precio con hasta 2 decimales
        ],
      ],
      maxPrice: [0,
        [
        Validators.min(0), // Precio debe ser positivo
        Validators.pattern(/^\d+(\.\d{1,2})?$/), // Precio con hasta 2 decimales
      ]],
      currency: ['', Validators.required],
      modelCode: ['', Validators.required],
      enabled: [false],
      residualModelValues: [this.residualValue],
    });
  }

  patchForm(data: any) {
    this.form.patchValue({});
  }

  openModal(residualValueData?: any, index?: number) {
    if (residualValueData) {
      this.modalService.data = { ...residualValueData, index };
    } else {
      this.modalService.data = null;
    }
    this.modalService.component = this.modalTemplate;
    this.modalService.openModal();
  }

  editResidualValue(residualValueData: any, index: number) {
    this.openModal(residualValueData, index);
  }

  deleteResidualValue(residualValueData: any) {
    const index = this.residualValue.indexOf(residualValueData);
    if (index !== -1) {
      this.residualValue.splice(index, 1);
    }
  }

  handleSave(residualValueData: any) {
    if (residualValueData.index !== undefined && residualValueData.index !== null) {
      this.residualValue[residualValueData.index] = residualValueData;
    } else {
      this.residualValue.push(residualValueData);
    }
    this.saveResidualValue.emit(this.residualValue);
    this.modalService.closeModal();
  }

  addResidualValue(data: any) {
    this.residualValue.push(data);
    this.saveResidualValue.emit(this.residualValue);
  }

  getBrands() {
    this.service.getBrand().subscribe({
      next: (response) => {
        this.brands = response.data
          .map((brand: any) => {
            return { label: brand.brand, id: brand.id };
          })
          .sort((a: any, b: any) => {
            const labelA = (a.label || '').toLowerCase();
            const labelB = (b.label || '').toLowerCase();
            return labelA.localeCompare(labelB);
          });
      },
    });
  }

  handleMaxPriceChange() {
    this.verifyFixedAmount(true);
    this.hideAmountInput('maxPrice');
    this.form.updateValueAndValidity();

  }

  // getSubBrands() {
  //   this.service.getSubBrand().subscribe({
  //     next: (response) => {
  //       this.subBrands = response.data.map((subBrand: any) => {
  //         return { label: subBrand.subBrand, id: subBrand.id };
  //       });
  //     },
  //   });
  // }

  getSubBrandsByIdBrand(idBrand: number) {
    this.service.getSubBrandsByIdBrand(idBrand).subscribe({
      next: (response: any) => {
        this.subBrands = response.data
          .map((subBrand: any) => {
            return { label: subBrand.subBrand, id: subBrand.id };
          })
          .sort((a: any, b: any) => {
            const labelA = (a.label || '').toLowerCase();
            const labelB = (b.label || '').toLowerCase();
            return labelA.localeCompare(labelB);
          });
      },
    });
  }

  cancel() {
    this.router.navigate(['/financial-product/list']);
  }

  save() {
    if (this.id) {
      this.updateProduct();
    } else {
      this.isValidNewProductThenSave()
    }
  }

  createProduct() {
    if (this.form.invalid) {
      return;
    }
    this.isLoading = true;
    const formData = this.form.value;
    this.service.createNewProduct(formData).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.showNotificationSuccess('El producto ha sido creado con éxito.');
        this.router.navigate(['/financial-product/list']);
      },
      error: (error) => {
        this.isLoading = false;
        this.showNotificationError('Ha ocurrido un error, el paquete financiero no se pudo crear.');
      },
    });
  }

  isValidNewProductThenSave() {
    this.service.validateNewProduct(this.form.getRawValue()).subscribe({
      next: (response: any) => {
        if(!response.status){
          const message = this.translateService.instant(`errors`)[response.error] ?? '';
          this.notificationsService.showMessage([message], 'error');
          return;
        }
        this.createProduct();
      },
      error: () => {}
    });
  }


  getModelById() {
    this.isLoading = true;
    this.service.getModelById(this.id).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.form.patchValue({
          ...response.data,
          brandId: response.data.subBrand.brand['id'],
          subBrandId: response.data.subBrand['id'],
          maxPrice: response.data?.maxPrice || 0,
        });
        const residualValues = response.data.residualModelValues ?? [];
        // Ordenar los valores residuales por el plazo menor a mayor
        this.residualValue = [...residualValues].sort((a, b) => Number(a.term) - Number(b.term));
        this.getFile(response.data.subBrand['id']);

        const brandId = response.data.subBrand.brand['id'];
        if (brandId) {
          this.getSubBrandsByIdBrand(brandId);
        }

        // if (response.data.price) {
        //   this.formatDisplayPrice(response.data.price);
        // }
      },
      error: (error) => {
        this.isLoading = false;
        this.showNotificationError('Ha ocurrido un error, el producto financiero no se pudo obtener.');
      },
    });
  }

  verifyFixedAmount(forceUpdate: boolean = false) {
    const maxPrice = this.form.controls['maxPrice'].value ?? 0;
    const isMaxPriceDifferentThanZero = maxPrice !== null && maxPrice !== "" && maxPrice !== 0;
    const updatedItems = this.residualValue.map(itemvalue =>{
      const isDaimlerFixedAmountZero = itemvalue?.daimlerFixedAmount === 0 || itemvalue?.daimlerFixedAmount === null || itemvalue?.daimlerFixedAmount === "" || itemvalue?.daimlerFixedAmount === undefined;
      const hasValueInDaimlerRate = itemvalue?.daimlerRate !== null && itemvalue?.daimlerRate !== 0;
      // Si hay precio maximo, hay valor residual daimler, pero no hay monto fijo, calcular el monto fijo
      if(isMaxPriceDifferentThanZero && hasValueInDaimlerRate && (isDaimlerFixedAmountZero || forceUpdate)){
        const fixedAmount = maxPrice * (itemvalue?.daimlerRate / 100);
        return { ...itemvalue, daimlerFixedAmount: Number(fixedAmount.toFixed(2)) };

        // Si hay valor residual daimler, pero no hay precio maximo, calcula el monto fijo a 0
      } else if(hasValueInDaimlerRate && isDaimlerFixedAmountZero) {
        return { ...itemvalue, daimlerFixedAmount: 0 };
      }
      return itemvalue;
    });
    this.residualValue = updatedItems;
  }

  updateProduct() {
    this.isLoading = true;
    const formData = this.form.getRawValue();
    formData.residualModelValues = this.residualValue;
    this.service.updateProduct(this.id, formData).subscribe({
      next: (response) => {
        this.isLoading = false;
        if(response.status) {
          this.showNotificationSuccess(
            'El producto ha sido actualizado con éxito.'
          );
          this.router.navigate(['/financial-product/list']);
        } else {
          const message = this.translateService.instant(`errors`)[response.error] ?? '';
          this.notificationsService.showMessage([message], 'error');
          return;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.showNotificationError('Ha ocurrido un error, el producto financiero no se pudo actualizar.');
      },
    });
  }

  searchBrandsAndSubBrands() {
    const modelType = this.form.get('modelType')?.value;
    if(modelType === productTypeEnum.accesorios){
      this.isLoading = true;
      this.form.controls['brandId'].reset();
      this.form.controls['subBrandId'].reset();
      this.productImg.set('');
      const brandFound = this.brands.find(brand =>
        brand.label.toLowerCase() === 'accesories' || brand.label.toLowerCase() === 'accesorio' || brand.label.toLowerCase() === 'accesorios'
      );
      this.form.controls['brandId'].setValue(brandFound.id);
      this.updateSubBrandsList(brandFound.id).add(()=>{
        this.form.controls['subBrandId'].setValue(this.subBrands[0].id);
      });
    }else {
      this.form.controls['brandId'].reset();
    }
  }

  filterBrands() {
    const brandId = this.form.get('brandId')?.value;
    this.form.controls['subBrandId'].reset();
    this.productImg.set('');
    this.form.updateValueAndValidity({ onlySelf: true });
    this.updateSubBrandsList(brandId);
  }

  updateSubBrandsList(brandId: number){
    this.isLoading = true;
    return this.service.getSubBrandsByIdBrand(brandId).pipe(take(1), finalize(() => this.isLoading = false)).subscribe({
      next: (response: any) => {
        this.subBrands = response.data
          .map((subBrand: any) => {
            return { label: subBrand.subBrand, id: subBrand.id };
          })
          .sort((a: any, b: any) => {
            const labelA = (a.label || '').toLowerCase();
            const labelB = (b.label || '').toLowerCase();
            return labelA.localeCompare(labelB);
          });
        this.setDefaultSubBrand(this.subBrands);
      },
      error: (error) => {
        this.showNotificationError('Ha ocurrido un error, No se pudo obtener las submarcas.');
      },
    });
  }

  setDefaultSubBrand(subBrands: any[]) {
    if(subBrands?.length >= 1 && subBrands[0]?.id){
      this.form.controls['subBrandId'].setValue(subBrands[0].id);
      this.getFile(Number(subBrands[0].id) || 0);
    }
  }

  get isYearMinValueNotValid() {
    return this.form.get('year')?.touched && this.form.get('year')?.hasError('min');
  }

  get isAccesoriesProduct() {
    return this.form.get('modelType')?.value === productTypeEnum.accesorios;
  }
}
